/**
 * Pure decisions for the user-settings modal's tab navigation: which tab a
 * raw value resolves to, and the variant class for a tab given the active one.
 * No `vue`/`ref`/IO — testable with a plain call + `expect`.
 */

export const SETTINGS_ITEMS = [
  'general',
  'account',
  'localization',
  'support'
] as const

export type SettingsItem = (typeof SETTINGS_ITEMS)[number]

/** Resolve a raw `?modals=` tab param to a valid tab, or `null` if unknown. */
export function parseSettingsTab(
  value: string | null | undefined
): SettingsItem | null {
  return SETTINGS_ITEMS.includes(value as SettingsItem)
    ? (value as SettingsItem)
    : null
}

/**
 * Base classes shared by every mobile tab-bar item (previously inline in
 * `tabClass`). The active/inactive fragment is decided by
 * {@link settingsTabVariantClass}; the shell composes them via `cn`.
 */
export const SETTINGS_TAB_BASE_CLASS =
  'flex cursor-pointer items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-sm transition-colors'

const SETTINGS_TAB_ACTIVE_CLASS = 'border-primary text-foreground font-medium'

const SETTINGS_TAB_INACTIVE_CLASS =
  'text-muted-foreground hover:text-foreground hover:border-border border-transparent'

/** The active vs inactive class fragment for a tab given the active tab. */
export function settingsTabVariantClass(
  value: string,
  activeItem: string
): string {
  return value === activeItem
    ? SETTINGS_TAB_ACTIVE_CLASS
    : SETTINGS_TAB_INACTIVE_CLASS
}
